<template>
  <v-app :class="{ 'dark-theme': isDarkMode }">
    <!-- Use Existing Dealer Header -->
    <HeaderDealerDashboard :userName="userName" :businessName="businessName" :isDarkMode="isDarkMode" />

    <!-- Main Dashboard Container -->
    <div class="dashboard-wrapper" :class="{ 'dark-mode': isDarkMode }">
      <!-- Dashboard Sub-Header with Logo and Dark Mode Toggle -->
      <header class="dashboard-subheader">
        <div class="header-left mt-5">
          <img src="@/assets/img/inventory-logo.png" alt="AutoSyncX" class="dashboard-logo" />
        </div>
        <div class="header-right">
          <v-btn icon small @click="toggleDarkMode" class="theme-toggle">
            <span class="material-icons-round">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</span>
          </v-btn>
        </div>
      </header>

      <!-- Main Content Grid -->
      <main class="dashboard-main">
        <!-- Left Column -->
        <div class="left-column">
          <!-- Overview Card -->
          <div class="glass-card overview-card">
            <h3 class="card-title">Overview</h3>
            <router-link to="/dealers/inventory" class="overview-item clickable">
              <span class="overview-label">Available Inventory</span>
              <div class="overview-value">
                <span class="material-icons-round mini-chart">show_chart</span>
                <span class="value">{{ dashboardData.stats.live_vehicles }}</span>
              </div>
            </router-link>
            <div class="overview-item">
              <span class="overview-label">Total Leads</span>
              <div class="overview-value">
                <span class="material-icons-round check-icon">contact_mail</span>
                <span class="value">{{ dashboardData.stats.total_leads || dashboardData.stats.pending_leads || 0 }}</span>
              </div>
            </div>
            <div class="overview-item">
              <span class="overview-label">AutoSyncX™ Eligible</span>
              <div class="overview-value">
                <span class="material-icons-round check-icon">verified</span>
                <span class="value">{{ autosyncxCount }}</span>
              </div>
            </div>
            <div class="overview-item">
              <span class="overview-label">Commission This Month</span>
              <div class="overview-value sales">
                <span class="value">${{ formatCurrency(salesThisMonth) }}</span>
                <span class="commission-label">payout</span>
              </div>
            </div>
          </div>

          <!-- My Tasks Card -->
          <div class="glass-card tasks-card">
            <h3 class="card-title">Recent Activity</h3>
            <template v-if="tasks.length > 0">
              <div class="task-item" v-for="(task, index) in tasks" :key="index">
                <span class="task-text">{{ task.title }}</span>
                <span class="task-due" :class="task.dueClass">
                  {{ task.due }}
                  <span class="material-icons-round heart-icon">schedule</span>
                </span>
              </div>
            </template>
            <div v-else class="empty-state">
              <span class="material-icons-round" style="font-size: 32px; opacity: 0.3;">task_alt</span>
              <p style="font-size: 12px; margin-top: 8px;">No recent activity</p>
            </div>
          </div>

          <!-- Referring Agent Card (if referred by an agent) -->
          <div v-if="referringAgent" class="glass-card agent-referral-card">
            <h3 class="card-title">
              <span class="material-icons-round" style="vertical-align: middle; margin-right: 8px; color: #4ade80;">handshake</span>
              Your Referring Agent
            </h3>
            <div class="agent-info-section">
              <div class="agent-avatar">
                <img v-if="referringAgent.avatar" :src="wasabiUrl + referringAgent.avatar" alt="Agent" @error="handleAgentAvatarError" />
                <span v-else class="material-icons-round avatar-placeholder">person</span>
              </div>
              <div class="agent-details">
                <div class="agent-name">{{ referringAgent.name }}</div>
                <div class="agent-contact">
                  <a v-if="referringAgent.email" :href="'mailto:' + referringAgent.email" class="contact-link">
                    <span class="material-icons-round">email</span>
                    {{ referringAgent.email }}
                  </a>
                </div>
                <div class="agent-contact" v-if="referringAgent.phone">
                  <a :href="'tel:' + referringAgent.phone" class="contact-link">
                    <span class="material-icons-round">phone</span>
                    {{ referringAgent.phone }}
                  </a>
                </div>
              </div>
            </div>
            <div class="commission-info-box">
              <div class="commission-label">Commission Per Sale</div>
              <div class="commission-amount">${{ formatCurrency(referringAgent.commission_per_sale) }}</div>
              <div class="commission-note">This amount is paid to the agent for each successful vehicle sale through their referrals.</div>
            </div>
          </div>
        </div>

        <!-- Middle Column -->
        <div class="middle-column">
          <!-- Latest Leads Card -->
          <div class="glass-card leads-card">
            <div class="card-header">
              <h3 class="card-title">Latest Leads</h3>
              <router-link to="/dealers/leads" class="view-all-link">View All <span class="material-icons-round">chevron_right</span></router-link>
            </div>
            <template v-if="latestLeads.length > 0">
              <div class="lead-item" v-for="(lead, index) in latestLeads" :key="index">
                <div class="lead-image">
                  <img :src="getLeadImage(lead)" :alt="lead.vehicleName" @error="handleImageError($event)" />
                </div>
                <div class="lead-info">
                  <div class="lead-name">{{ lead.customerName }} <span v-if="lead.sold" class="sold-badge">Sold</span></div>
                  <div class="lead-vehicle">{{ lead.vehicleName || 'Vehicle Interest' }}</div>
                  <div class="lead-meta">{{ lead.timeAgo }} · {{ lead.source }}</div>
                </div>
                <div class="lead-details">
                  <span :class="['interest-type-badge', lead.interestType]">{{ getInterestTypeLabel(lead.interestType) }}</span>
                  <div class="lead-question">{{ lead.status === 'contacted' ? 'Contacted' : 'New Lead' }}</div>
                </div>
                <div class="lead-actions">
                  <span class="interest-badge" v-if="lead.autosyncEligible">AUTOSYNC ELIGIBLE</span>
                  <button class="view-details-btn" @click="openLeadDialog(lead)">View Details</button>
                </div>
              </div>
            </template>
            <div v-else class="empty-state">
              <span class="material-icons-round" style="font-size: 48px; opacity: 0.3;">people_outline</span>
              <p>No leads yet</p>
            </div>
          </div>

          <!-- Inventory Section -->
          <div class="glass-card inventory-card">
            <div class="card-header">
              <h3 class="card-title">Inventory</h3>
              <div class="inventory-tabs">
                <button :class="{ active: inventoryTab === 'stock' }" @click="inventoryTab = 'stock'">All Vehicles</button>
                <button :class="{ active: inventoryTab === 'short' }" @click="inventoryTab = 'short'">AutoSyncX™</button>
              </div>
              <router-link to="/dealers/inventory" class="view-all-link">View All <span class="material-icons-round">chevron_right</span></router-link>
            </div>
            <div class="inventory-filters">
              <select v-model="inventoryFilter.year">
                <option value="">All Years</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
                <option value="2022">2022</option>
              </select>
              <select v-model="inventoryFilter.make">
                <option value="">All Makes</option>
                <option value="Toyota">Toyota</option>
                <option value="Honda">Honda</option>
                <option value="Ford">Ford</option>
              </select>
              <span class="filter-label active">{{ inventoryVehicles.length }} Vehicles</span>
            </div>
            <template v-if="inventoryVehicles.length > 0">
              <div class="inventory-grid">
                <div class="inventory-item" v-for="(vehicle, index) in inventoryVehicles" :key="index">
                  <div class="vehicle-image">
                    <img :src="vehicle.image || require('@/assets/img/no-vehicle.jpg')" :alt="vehicle.name" />
                  </div>
                  <div class="vehicle-name">{{ vehicle.name }}</div>
                  <div class="vehicle-badge" :class="vehicle.badgeClass">{{ vehicle.badge }}</div>
                  <div class="vehicle-actions">
                    <button class="action-btn" @click="openEditDialog(vehicle)">Edit</button>
                    <button class="action-btn primary" v-if="vehicle.canMarkSold" @click="markAsSold(vehicle)">Mark Sold</button>
                    <button class="action-btn primary" v-else>Sold</button>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="empty-state">
              <span class="material-icons-round" style="font-size: 48px; opacity: 0.3;">inventory_2</span>
              <p>No vehicles in inventory</p>
            </div>
          </div>
        </div>

        <!-- Right Column - Quick Stats -->
        <div class="right-column">
          <div class="glass-card stats-card">
            <h3 class="card-title">Quick Stats</h3>

            <!-- Monthly Leads Chart -->
            <div class="stat-section">
              <div class="stat-label">Monthly Leads</div>
              <div class="mini-chart-container">
                <svg class="line-chart" viewBox="0 0 200 60">
                  <polyline
                    fill="none"
                    stroke-width="2"
                    points="0,50 30,45 60,35 90,40 120,25 150,30 180,20 200,15"
                  />
                </svg>
                <div class="chart-labels">
                  <span>Week 1</span>
                  <span>Week 2</span>
                  <span>Week 3</span>
                </div>
              </div>
            </div>

            <!-- Commission Stats -->
            <div class="stat-row">
              <div class="stat-box">
                <div class="stat-icon">
                  <span class="material-icons-round">payments</span>
                </div>
                <div class="stat-content">
                  <span class="stat-label">Commission This Month</span>
                  <span class="stat-value">${{ formatCurrency(dashboardData.commission_overview.current_month) }}</span>
                </div>
              </div>
              <div class="stat-box">
                <div class="stat-icon">
                  <span class="material-icons-round">people</span>
                </div>
                <div class="stat-content">
                  <span class="stat-label">Active Agents</span>
                  <span class="stat-value">{{ dashboardData.stats.active_agents }}</span>
                </div>
              </div>
            </div>

            <!-- Inventory Progress -->
            <div class="stat-section circular-stat">
              <div class="circular-content">
                <span class="stat-label">Inventory Status</span>
                <div class="circular-progress">
                  <svg viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" stroke-width="8" fill="none" />
                    <circle cx="50" cy="50" r="40" stroke="#22c55e" stroke-width="8" fill="none"
                      :stroke-dasharray="251" :stroke-dashoffset="251 - (inventoryProgress * 2.51)" stroke-linecap="round" />
                  </svg>
                  <div class="circular-value">
                    <span class="label">Vehicles</span>
                    <span class="value">{{ dashboardData.stats.live_vehicles }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Active Agents -->
            <div class="stat-section bar-stat">
              <div class="bar-header">
                <span class="stat-label">Agent Performance</span>
                <span class="sale-badge">{{ agentPerformanceData.active_agents }} Active</span>
              </div>
              <div class="bar-chart">
                <div class="bar" style="height: 30%"></div>
                <div class="bar" style="height: 50%"></div>
                <div class="bar" style="height: 70%"></div>
                <div class="bar" style="height: 45%"></div>
                <div class="bar active" style="height: 85%"></div>
              </div>
              <div class="bar-value">{{ agentPerformanceData.top_agent_leads }}</div>
              <div class="stat-label" style="text-align: center; margin-top: 4px;">Top Agent Leads</div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Edit Vehicle Dialog -->
    <v-dialog v-model="vehicleDialog" max-width="1200" persistent scrollable>
      <v-card>
        <v-card-title class="blue white--text">
          <span class="material-icons-round mr-2">edit</span>
          Edit Vehicle
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeVehicleDialog">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 75vh; overflow-y: auto;">
          <v-tabs v-model="formTab" background-color="transparent" color="primary" grow>
            <v-tab>Vehicle Info</v-tab>
            <v-tab>Price & Cost</v-tab>
            <v-tab>Photos</v-tab>
          </v-tabs>
          <v-tabs-items v-model="formTab" class="mt-4">
            <!-- Vehicle Info Tab -->
            <v-tab-item>
              <v-form ref="vehicleForm" v-model="formValid">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="vehicleForm.vin" label="VIN *" outlined dense :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="vehicleForm.stock_number" label="Stock Number" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.year" label="Year *" outlined dense type="number" :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.make" label="Make *" outlined dense :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.model" label="Model *" outlined dense :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="vehicleForm.trim" label="Trim" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select v-model="vehicleForm.body_style" :items="bodyStyleOptions" label="Body Style" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.odometer" label="Mileage" outlined dense type="number" suffix="mi" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="vehicleForm.transmission" :items="transmissionOptions" label="Transmission" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="vehicleForm.fuel_type" :items="fuelTypeOptions" label="Fuel Type" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.exterior_color" label="Exterior Color" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.interior_color" label="Interior Color" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.engine" label="Engine" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="vehicleForm.drivetrain" :items="drivetrainOptions" label="Drivetrain" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.doors" label="Doors" outlined dense type="number" color="#e91e63"></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-tab-item>
            <!-- Price & Cost Tab -->
            <v-tab-item>
              <v-row>
                <v-col cols="12" md="6">
                  <h5 class="text-h6 font-weight-bold mb-4">Price Info</h5>
                  <v-text-field v-model="vehicleForm.amount" label="Asking Price *" outlined dense type="number" prefix="$" :rules="[rules.required]" color="#e91e63"></v-text-field>
                  <v-text-field v-model="vehicleForm.special_price" label="Special Price" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <h5 class="text-h6 font-weight-bold mb-4">Cost Info</h5>
                  <v-text-field v-model="vehicleForm.vehicle_cost" label="Vehicle Cost" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                  <v-text-field v-model="vehicleForm.packs" label="Packs" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                  <v-text-field v-model="vehicleForm.total_tax" label="Total Tax" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-divider class="my-4"></v-divider>
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="text-center pa-4" style="background: #f5f5f5; border-radius: 8px;">
                        <p class="text-subtitle-2 grey--text mb-1">Total Cost</p>
                        <p class="text-h5 font-weight-bold error--text">{{ currencyFormat(totalCost) }}</p>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="text-center pa-4" style="background: #f5f5f5; border-radius: 8px;">
                        <p class="text-subtitle-2 grey--text mb-1">Vehicle Price</p>
                        <p class="text-h5 font-weight-bold" style="color: #1867c0">{{ currencyFormat(vehicleForm.amount || 0) }}</p>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="text-center pa-4" style="background: #f5f5f5; border-radius: 8px;">
                        <p class="text-subtitle-2 grey--text mb-1">Potential Profit</p>
                        <p class="text-h5 font-weight-bold" :class="potentialProfit >= 0 ? 'success--text' : 'error--text'">{{ currencyFormat(potentialProfit) }}</p>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-tab-item>
            <!-- Photos Tab -->
            <v-tab-item>
              <div class="mb-4">
                <v-btn color="primary" @click="$refs.imageInput.click()" :loading="uploadingImages">
                  <span class="material-icons-round mr-2">add_photo_alternate</span>
                  Upload Photos
                </v-btn>
                <input type="file" ref="imageInput" style="display: none;" multiple accept="image/*" @change="uploadImages" />
              </div>
              <v-row v-if="vehicleImages.length > 0">
                <v-col cols="6" md="3" v-for="(img, idx) in vehicleImages" :key="idx">
                  <v-card class="pa-1" outlined>
                    <v-img :src="img.image || img.url" height="120" contain></v-img>
                    <v-btn icon x-small color="error" class="delete-img-btn" @click="deleteImage(img)">
                      <v-icon small class="material-icons-round">delete</v-icon>
                    </v-btn>
                  </v-card>
                </v-col>
              </v-row>
              <div v-else class="text-center py-8 grey--text">
                <span class="material-icons-round" style="font-size: 48px;">photo_library</span>
                <p class="mt-2">No photos uploaded yet</p>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeVehicleDialog">Cancel</v-btn>
          <v-btn color="blue" dark :loading="saving" :disabled="!formValid" @click="saveVehicle">
            Update Vehicle
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Lead Details Dialog -->
    <v-dialog v-model="leadDialog" max-width="600" :dark="isDarkMode">
      <v-card :dark="isDarkMode" class="lead-detail-card">
        <v-card-title class="headline d-flex justify-space-between align-center">
          <span>Lead Details</span>
          <v-chip
            small
            :color="getInterestTypeColor(selectedLead.interestType)"
            text-color="white"
          >
            {{ getInterestTypeLabel(selectedLead.interestType) }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Customer Info -->
            <v-col cols="12">
              <div class="section-title">
                <v-icon class="material-icons-round mr-2">person</v-icon>
                Customer Information
              </div>
              <v-divider class="mb-3"></v-divider>
              <v-row dense>
                <v-col cols="6">
                  <div class="info-label">Name</div>
                  <div class="info-value">{{ selectedLead.customerName || 'N/A' }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="info-label">Email</div>
                  <div class="info-value">
                    <a v-if="selectedLead.customerEmail" :href="'mailto:' + selectedLead.customerEmail">{{ selectedLead.customerEmail }}</a>
                    <span v-else>N/A</span>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="info-label">Phone</div>
                  <div class="info-value">
                    <a v-if="selectedLead.customerPhone" :href="'tel:' + selectedLead.customerPhone">{{ selectedLead.customerPhone }}</a>
                    <span v-else>N/A</span>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="info-label">Status</div>
                  <div class="info-value">
                    <v-chip x-small :color="selectedLead.status === 'contacted' ? 'success' : 'warning'">
                      {{ selectedLead.status === 'contacted' ? 'Contacted' : 'New Lead' }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <!-- Vehicle Info -->
            <v-col cols="12" class="mt-4">
              <div class="section-title">
                <v-icon class="material-icons-round mr-2">directions_car</v-icon>
                Vehicle Interest
              </div>
              <v-divider class="mb-3"></v-divider>
              <v-row dense>
                <v-col cols="12" md="4" v-if="selectedLead.vehicleImage">
                  <v-img :src="selectedLead.vehicleImage" height="120" contain class="vehicle-preview-img"></v-img>
                </v-col>
                <v-col :cols="selectedLead.vehicleImage ? 8 : 12">
                  <v-row dense>
                    <v-col cols="12">
                      <div class="info-label">Vehicle</div>
                      <div class="info-value vehicle-name-large">{{ selectedLead.vehicleName || 'N/A' }}</div>
                    </v-col>
                    <v-col cols="6">
                      <div class="info-label">VIN</div>
                      <div class="info-value">{{ selectedLead.vehicleVin || 'N/A' }}</div>
                    </v-col>
                    <v-col cols="6">
                      <div class="info-label">Price</div>
                      <div class="info-value">${{ formatCurrency(selectedLead.vehiclePrice) }}</div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>

            <!-- Activity Info -->
            <v-col cols="12" class="mt-4">
              <div class="section-title">
                <v-icon class="material-icons-round mr-2">history</v-icon>
                Activity
              </div>
              <v-divider class="mb-3"></v-divider>
              <v-row dense>
                <v-col cols="6">
                  <div class="info-label">Source</div>
                  <div class="info-value">{{ selectedLead.source || 'Direct' }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="info-label">Date</div>
                  <div class="info-value">{{ selectedLead.timeAgo }}</div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="leadDialog = false">Close</v-btn>
          <v-btn v-if="selectedLead.customerPhone" color="success" dark :href="'tel:' + selectedLead.customerPhone">
            <v-icon class="material-icons-round mr-1" small>phone</v-icon> Call
          </v-btn>
          <v-btn v-if="selectedLead.customerEmail" color="primary" dark :href="'mailto:' + selectedLead.customerEmail">
            <v-icon class="material-icons-round mr-1" small>email</v-icon> Email
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mark as Sold Dialog -->
    <v-dialog v-model="markSoldDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h5 success--text">
          <v-icon class="material-icons-round mr-2" color="success">sell</v-icon>
          Mark Vehicle as Sold
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="selectedVehicleForSale" class="mb-4">
            <div class="text-subtitle-1 font-weight-bold">{{ selectedVehicleForSale.name }}</div>
            <div class="text-caption grey--text">VIN: {{ selectedVehicleForSale.vin || 'N/A' }}</div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="text-subtitle-2 mb-2">Who sold this vehicle?</div>

          <v-radio-group v-model="soldByType" class="mt-2">
            <v-radio label="Sold by Referring Agent" value="agent"></v-radio>
            <v-radio label="Sold by Outside/Other" value="outside"></v-radio>
          </v-radio-group>

          <v-select
            v-if="soldByType === 'agent'"
            v-model="selectedAgentForSale"
            :items="availableAgentsForSale"
            item-text="name"
            item-value="user_id"
            label="Select Agent"
            outlined
            dense
            :loading="loadingAgents"
            :rules="[v => !!v || 'Please select an agent']"
            class="mt-2"
          >
            <template v-slot:item="{ item }">
              <v-list-item-avatar size="32" color="primary">
                <span class="white--text text-caption">{{ getInitials(item.name) }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>

          <v-text-field
            v-model="saleReference"
            label="Sale Reference (Optional)"
            placeholder="e.g., Invoice #12345"
            outlined
            dense
            class="mt-2"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeMarkSoldDialog">Cancel</v-btn>
          <v-btn
            color="success"
            dark
            @click="confirmMarkAsSold"
            :loading="markingSold"
            :disabled="soldByType === 'agent' && !selectedAgentForSale"
          >
            <v-icon class="material-icons-round mr-1" small>check</v-icon>
            Confirm Sale
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'

export default {
  name: 'DealerDashboard',
  components: {
    HeaderDealerDashboard
  },
  data() {
    return {
      isDarkMode: true,
      userName: '',
      businessName: '',
      dealerStatus: '0',
      loading: true,
      // Dashboard stats from API
      dashboardData: {
        stats: {
          live_vehicles: 0,
          active_agents: 0,
          pending_leads: 0,
          unpaid_commissions: '0.00'
        },
        commission_overview: {
          current_month: '0.00',
          last_month: '0.00',
          total_earned: '0.00',
          pending_payout: '0.00'
        },
        recent_activity: []
      },
      // Inventory stats from API
      inventoryStats: {
        live_vehicles: 0,
        pending_approval: 0,
        last_sync: 'Loading...'
      },
      // Commission data from API
      commissionData: {
        flat_rate: 500,
        tiered_rate: 3,
        tiered_threshold: 25
      },
      // Agent performance from API
      agentPerformanceData: {
        top_agent_name: 'No agents',
        top_agent_leads: 0,
        avg_response_time: 0,
        active_agents: 0
      },
      // Referring agent info (if dealer was referred by an agent)
      referringAgent: null,
      wasabiUrl: process.env.VUE_APP_WASBI_URL || '',
      // Tasks (will be from API when available)
      tasks: [],
      // Latest Leads (from API)
      latestLeads: [],
      // Inventory vehicles (from API)
      inventoryTab: 'stock',
      inventoryFilter: { year: '', make: '', model: '' },
      inventoryVehicles: [],
      // Quick Stats calculated from API data
      salesThisMonth: 0,
      referralCommission: 0,
      totalLeadsThisMonth: 0,
      directDealerPurchase: 0,
      // Vehicle Edit Dialog
      vehicleDialog: false,
      formTab: 0,
      formValid: false,
      saving: false,
      uploadingImages: false,
      vehicleImages: [],
      vehicleForm: {
        id: null, vin: '', stock_number: '', year: '', make: '', model: '', trim: '',
        body_style: '', amount: '', odometer: '', transmission: '', exterior_color: '',
        interior_color: '', engine: '', fuel_type: '', drivetrain: '', doors: '',
        vehicle_cost: '', packs: '', total_tax: '', special_price: ''
      },
      rules: {
        required: v => !!v || 'This field is required'
      },
      bodyStyleOptions: ['Coupe', 'Convertible', 'Sedan', 'Hybrid/Electric', 'Crossover/SUV', 'Truck', '4WD/AWD', 'Van', 'Wagon', 'Hatchback'],
      transmissionOptions: ['Automatic', 'Manual', 'CVT'],
      fuelTypeOptions: ['Gasoline', 'Diesel', 'Electric', 'Hybrid', 'Plug-in Hybrid'],
      drivetrainOptions: ['FWD', 'RWD', 'AWD', '4WD'],
      // Lead Details Dialog
      leadDialog: false,
      selectedLead: {},
      // Mark as Sold Dialog
      markSoldDialog: false,
      selectedVehicleForSale: null,
      soldByType: 'agent',
      selectedAgentForSale: null,
      saleReference: '',
      availableAgentsForSale: [],
      loadingAgents: false,
      markingSold: false
    }
  },
  computed: {
    autosyncxCount() {
      return this.inventoryVehicles.filter(v => v.autosync_eligible).length || 0
    },
    inventoryProgress() {
      const total = this.dashboardData.stats.live_vehicles || 0
      return total > 0 ? Math.min(total, 100) : 0
    },
    totalCost() {
      return Number(this.vehicleForm.vehicle_cost || 0) + Number(this.vehicleForm.packs || 0) + Number(this.vehicleForm.total_tax || 0)
    },
    potentialProfit() {
      return Number(this.vehicleForm.amount || 0) - this.totalCost
    }
  },
  beforeCreate() {
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  mounted() {
    this.loadUserData()
    this.loadDarkModePreference()
  },
  methods: {
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Your Business'
      this.dealerStatus = localStorage.getItem('dealer_status') || '0'
      this.loadAllData()
    },
    loadDarkModePreference() {
      const saved = localStorage.getItem('dealer_dark_mode')
      this.isDarkMode = saved !== null ? saved === 'true' : true
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode
      localStorage.setItem('dealer_dark_mode', this.isDarkMode)
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('en-US')
    },
    async loadAllData() {
      this.loading = true
      await Promise.all([
        this.loadDashboardData(),
        this.loadInventoryData(),
        this.loadLatestLeads()
      ])
      this.loading = false
    },
    async loadDashboardData() {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          this.$router.push('/dealers/login')
          return
        }
        const response = await axios.get('/api/dealer/dashboard-data', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          const data = response.data.data
          this.userName = data.dealer_info?.name || this.userName
          this.businessName = data.dealer_info?.business_name || this.businessName
          this.dealerStatus = data.dealer_info?.dealer_status || this.dealerStatus

          if (data.stats) {
            this.dashboardData.stats = data.stats
          }
          if (data.commission_overview) {
            this.dashboardData.commission_overview = data.commission_overview
            this.referralCommission = parseFloat(data.commission_overview.current_month || 0)
            this.salesThisMonth = parseFloat(data.commission_overview.total_earned || 0)
          }
          if (data.inventory_stats) {
            this.inventoryStats = data.inventory_stats
          }
          if (data.commission_data) {
            this.commissionData = data.commission_data
          }
          if (data.agent_performance_data) {
            this.agentPerformanceData = data.agent_performance_data
          }
          if (data.recent_activity) {
            this.dashboardData.recent_activity = data.recent_activity
            // Convert recent activity to tasks format
            this.tasks = data.recent_activity.slice(0, 3).map(activity => ({
              title: activity.description || activity.action,
              due: this.getRelativeTime(activity.created_at),
              dueClass: this.getDueClass(activity.created_at)
            }))
          }
          // Load referring agent info if available
          if (data.referring_agent) {
            this.referringAgent = data.referring_agent
          }
        }
      } catch (error) {
        console.error('Error loading dashboard:', error)
      }
    },
    handleAgentAvatarError(event) {
      event.target.style.display = 'none'
    },
    async loadInventoryData() {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.post('/api/dealer/get-inventory', {
          offset: 0,
          limit: 6
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          const vehicles = response.data.data || []
          this.inventoryVehicles = vehicles.map(v => ({
            id: v.id,
            image: v.images?.[0]?.image || '',
            name: `${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim() || 'Unknown Vehicle',
            badge: v.autosync_eligible ? 'AutoSyncX™ Program Eligible' : 'Available Through Our Network',
            badgeClass: v.autosync_eligible ? 'autosync' : 'network',
            canEdit: true,
            canMarkSold: v.is_sold_out !== 1,
            autosync_eligible: v.autosync_eligible || false
          }))
        }
      } catch (error) {
        console.error('Error loading inventory:', error)
      }
    },
    async loadLatestLeads() {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.get('/api/dealer/leads?limit=4', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          const leads = response.data.data || []
          this.latestLeads = leads.map(lead => ({
            id: lead.id,
            customerName: lead.customer_name || lead.name || 'Unknown',
            customerEmail: lead.customer_email || '',
            customerPhone: lead.customer_phone || '',
            vehicleName: lead.vehicle_name || `${lead.year || ''} ${lead.make || ''} ${lead.model || ''}`.trim(),
            vehicleImage: lead.vehicle_image || '',
            vehicleVin: lead.vehicle_vin || '',
            vehiclePrice: lead.vehicle_price || 0,
            vehicleId: lead.vehicle_id,
            interestType: lead.interest_type || 'interested',
            timeAgo: this.getRelativeTime(lead.created_at),
            createdAt: lead.created_at,
            source: lead.source || 'Direct',
            status: lead.status || 'new',
            autosyncEligible: lead.autosync_eligible || false,
            sold: lead.status === 'sold'
          }))
          this.totalLeadsThisMonth = response.data.total || leads.length
        }
      } catch (error) {
        console.error('Error loading leads:', error)
        // Keep empty array on error
      }
    },
    getRelativeTime(dateString) {
      if (!dateString) return 'Today'
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      return `${Math.floor(diffDays / 7)} weeks ago`
    },
    getDueClass(dateString) {
      if (!dateString) return 'today'
      const date = new Date(dateString)
      const now = new Date()
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
      return diffDays === 0 ? 'today' : 'upcoming'
    },
    openLeadDialog(lead) {
      this.selectedLead = { ...lead }
      this.leadDialog = true
    },
    getLeadImage(lead) {
      if (lead.vehicleImage) {
        return lead.vehicleImage
      }
      try {
        return require('@/assets/img/no-vehicle.jpg')
      } catch {
        return ''
      }
    },
    handleImageError(event) {
      try {
        event.target.src = require('@/assets/img/no-vehicle.jpg')
      } catch {
        event.target.style.display = 'none'
      }
    },
    getInterestTypeLabel(type) {
      const labels = {
        'viewed': 'Viewed',
        'clicked': 'Clicked Details',
        'interested': 'Interested'
      }
      return labels[type] || 'Unknown'
    },
    getInterestTypeColor(type) {
      const colors = {
        'viewed': 'grey',
        'clicked': 'blue',
        'interested': 'success'
      }
      return colors[type] || 'grey'
    },
    currencyFormat(value) {
      return '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    getEmptyForm() {
      return {
        id: null, vin: '', stock_number: '', year: '', make: '', model: '', trim: '',
        body_style: '', amount: '', odometer: '', transmission: '', exterior_color: '',
        interior_color: '', engine: '', fuel_type: '', drivetrain: '', doors: '',
        vehicle_cost: '', packs: '', total_tax: '', special_price: ''
      }
    },
    async openEditDialog(vehicle) {
      // Fetch full vehicle details from API
      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(`/api/dealer/vehicle/${vehicle.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          this.vehicleForm = { ...response.data.data }
          this.vehicleImages = response.data.data.images || []
        } else {
          // Fallback to basic data from list
          this.vehicleForm = { ...vehicle }
          this.vehicleImages = []
        }
      } catch (error) {
        console.error('Error fetching vehicle details:', error)
        this.vehicleForm = { ...vehicle }
        this.vehicleImages = []
      }
      this.formTab = 0
      this.vehicleDialog = true
    },
    closeVehicleDialog() {
      this.vehicleDialog = false
      this.formTab = 0
      this.vehicleForm = this.getEmptyForm()
      this.vehicleImages = []
    },
    async saveVehicle() {
      if (!this.$refs.vehicleForm.validate()) return
      try {
        this.saving = true
        const token = localStorage.getItem('token')
        const payload = { ...this.vehicleForm }
        const response = await axios.post('/api/dealer/save-vehicle', payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          this.$toast.success('Vehicle updated!')
          this.closeVehicleDialog()
          this.loadInventoryData()
          this.loadDashboardData()
        } else {
          this.$toast.error(response.data.message || 'Error saving vehicle')
        }
      } catch (error) {
        console.error('Error saving vehicle:', error)
        this.$toast.error('Error saving vehicle')
      } finally {
        this.saving = false
      }
    },
    async uploadImages(event) {
      const files = event.target.files
      if (!files || files.length === 0) return
      if (!this.vehicleForm.id) {
        this.$toast.error('Please save the vehicle first before uploading images')
        return
      }
      try {
        this.uploadingImages = true
        const token = localStorage.getItem('token')
        const formData = new FormData()
        for (let i = 0; i < files.length; i++) {
          formData.append('files[]', files[i])
        }
        const response = await axios.post(`/api/dealer/vehicle/${this.vehicleForm.id}/upload-images`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        })
        if (response.data.flag === 1) {
          this.$toast.success('Images uploaded!')
          this.refreshVehicleImages()
        } else {
          this.$toast.error(response.data.message || 'Error uploading images')
        }
      } catch (error) {
        console.error('Error uploading images:', error)
        this.$toast.error('Error uploading images')
      } finally {
        this.uploadingImages = false
        this.$refs.imageInput.value = ''
      }
    },
    async refreshVehicleImages() {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(`/api/dealer/vehicle/${this.vehicleForm.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          this.vehicleImages = response.data.data.images || []
        }
      } catch (error) {
        console.error('Error refreshing images:', error)
      }
    },
    async deleteImage(img) {
      try {
        const token = localStorage.getItem('token')
        await axios.post(`/api/dealer/vehicle/delete-image/${img.id}`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        this.$toast.success('Image deleted!')
        this.refreshVehicleImages()
      } catch (error) {
        console.error('Error deleting image:', error)
        this.$toast.error('Error deleting image')
      }
    },
    async markAsSold(vehicle) {
      // Open dialog to select who sold the vehicle
      this.selectedVehicleForSale = vehicle
      this.soldByType = 'agent'
      this.selectedAgentForSale = null
      this.saleReference = vehicle.name || ''
      this.markSoldDialog = true

      // Load available agents
      await this.loadAvailableAgentsForSale()
    },
    async loadAvailableAgentsForSale() {
      try {
        this.loadingAgents = true
        const token = localStorage.getItem('token')
        const response = await axios.get('/api/dealer/referring-agents', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          this.availableAgentsForSale = (response.data.data.agents || []).map(agent => ({
            user_id: agent.user_id,
            name: agent.first_name + ' ' + agent.last_name,
            email: agent.email
          }))
        } else {
          this.availableAgentsForSale = []
        }
      } catch (error) {
        console.error('Error loading agents:', error)
        this.availableAgentsForSale = []
      } finally {
        this.loadingAgents = false
      }
    },
    closeMarkSoldDialog() {
      this.markSoldDialog = false
      this.selectedVehicleForSale = null
      this.soldByType = 'agent'
      this.selectedAgentForSale = null
      this.saleReference = ''
    },
    async confirmMarkAsSold() {
      try {
        this.markingSold = true
        const token = localStorage.getItem('token')

        const payload = {
          id: this.selectedVehicleForSale.id,
          sold_by_type: this.soldByType,
          sale_reference: this.saleReference
        }

        // Add agent info if sold by agent
        if (this.soldByType === 'agent' && this.selectedAgentForSale) {
          payload.agent_user_id = this.selectedAgentForSale
        }

        const response = await axios.post('/api/dealer/mark-vehicle-sold', payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Vehicle marked as sold!')
          if (this.soldByType === 'agent' && response.data.commission_created) {
            this.$toast.info('Commission created for referring agent')
          }
          this.closeMarkSoldDialog()
          this.loadInventoryData()
          this.loadDashboardData()
        } else {
          this.$toast.error(response.data.message || 'Error marking vehicle as sold')
        }
      } catch (error) {
        console.error('Error marking vehicle as sold:', error)
        this.$toast.error('Error marking vehicle as sold')
      } finally {
        this.markingSold = false
      }
    },
    getInitials(name) {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    }
  }
}
</script>

<style scoped>
/* Dashboard Wrapper */
.dashboard-wrapper {
  min-height: calc(100vh - 80px);
  margin-left: 250px;
  padding-top: 80px;
  background: url('~@/assets/img/skyline-bg.png') no-repeat center bottom;
  background-size: cover;
  background-color: #f0f4f8;
  color: #333;
}
.dashboard-wrapper.dark-mode {
  background: url('~@/assets/img/skyline-bg-dark.png') no-repeat center bottom;
  background-size: cover;
  background-color: #0f0f23;
  color: #fff;
}
</style>

<style>
/* Global Dark Theme for v-app */
.v-application.dark-theme {
  background-color: #0f0f23 !important;
}
.v-application.dark-theme .v-main {
  background-color: #0f0f23 !important;
}
</style>

<style scoped>

/* Dashboard Sub-Header */
.dashboard-subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  position: relative;
  z-index: 10;
}
.dashboard-logo {
  height: 50px;
  object-fit: contain;
}
.header-right { display: flex; align-items: center; gap: 16px; }
.theme-toggle { color: #333; }
.dark-mode .theme-toggle { color: #fff; }

/* Main Grid */
.dashboard-main {
  display: grid;
  grid-template-columns: 280px 1fr 300px;
  gap: 24px;
  padding: 0 32px 32px;
  position: relative;
  z-index: 1;
}

/* Glass Card - Light Mode */
.glass-card {
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
/* Glass Card - Dark Mode */
.dark-mode .glass-card {
  background: rgba(30, 30, 60, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(138, 43, 226, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}
.card-title { font-size: 16px; font-weight: 600; margin-bottom: 16px; color: #1e293b; }
.dark-mode .card-title { color: #fff; }

/* Overview Card */
.overview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
  text-decoration: none;
}
.dark-mode .overview-item { border-bottom-color: rgba(255,255,255,0.1); }
.overview-item:last-child { border-bottom: none; }
.overview-item.clickable {
  cursor: pointer;
  transition: background-color 0.2s ease, padding 0.2s ease;
  margin: 0 -12px;
  padding: 12px;
  border-radius: 8px;
}
.overview-item.clickable:hover {
  background-color: rgba(168, 85, 247, 0.1);
}
.dark-mode .overview-item.clickable:hover {
  background-color: rgba(168, 85, 247, 0.2);
}
.overview-label { font-size: 14px; color: #64748b; }
.dark-mode .overview-label { color: rgba(255,255,255,0.7); }
.overview-value { display: flex; align-items: center; gap: 8px; }
.overview-value .value { font-size: 24px; font-weight: 700; color: #1e293b; }
.dark-mode .overview-value .value { color: #fff; }
.overview-value .mini-chart { color: #a855f7; font-size: 20px; }
.overview-value .check-icon { color: #22c55e; font-size: 18px; }
.overview-value.sales .commission-label { font-size: 10px; color: #94a3b8; display: block; }
.dark-mode .overview-value.sales .commission-label { color: rgba(255,255,255,0.5); }

/* Tasks Card */
.tasks-card .task-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
}
.dark-mode .tasks-card .task-item { border-bottom-color: rgba(255,255,255,0.08); }
.task-item:last-child { border-bottom: none; }
.task-text { font-size: 13px; color: #334155; }
.dark-mode .task-text { color: rgba(255,255,255,0.9); }
.task-due { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #64748b; }
.dark-mode .task-due { color: rgba(255,255,255,0.6); }
.task-due .heart-icon { font-size: 14px; color: #ec4899; }
.task-due.today { color: #f97316; }

/* Leads Card */
.leads-card .card-header, .inventory-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.view-all-link { display: flex; align-items: center; gap: 4px; font-size: 12px; text-decoration: none; color: #6366f1; }
.dark-mode .view-all-link { color: #a855f7; }
.lead-item {
  display: grid;
  grid-template-columns: 80px 1fr 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}
.dark-mode .lead-item { border-bottom-color: rgba(255,255,255,0.08); }
.lead-item:last-child { border-bottom: none; }
.lead-image img { width: 70px; height: 50px; object-fit: cover; border-radius: 8px; background: #f1f5f9; }
.lead-name { font-weight: 600; font-size: 14px; display: flex; align-items: center; gap: 8px; color: #1e293b; }
.dark-mode .lead-name { color: #fff; }
.sold-badge { background: #22c55e; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 10px; }
.lead-vehicle { font-size: 13px; color: #475569; }
.dark-mode .lead-vehicle { color: rgba(255,255,255,0.9); }
.lead-meta { font-size: 11px; color: #94a3b8; }
.dark-mode .lead-meta { color: rgba(255,255,255,0.5); }
.lead-program { font-size: 11px; color: #a855f7; }
.lead-question { font-size: 11px; color: #94a3b8; }
.dark-mode .lead-question { color: rgba(255,255,255,0.5); }

/* Interest Type Badges */
.interest-type-badge {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 4px;
}
.interest-type-badge.viewed {
  background: rgba(156, 163, 175, 0.2);
  color: #6b7280;
}
.interest-type-badge.clicked {
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
}
.interest-type-badge.interested {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}
.dark-mode .interest-type-badge.viewed { background: rgba(156, 163, 175, 0.3); color: #9ca3af; }
.dark-mode .interest-type-badge.clicked { background: rgba(59, 130, 246, 0.25); color: #60a5fa; }
.dark-mode .interest-type-badge.interested { background: rgba(34, 197, 94, 0.25); color: #4ade80; }

.interest-badge {
  background: rgba(168, 85, 247, 0.15);
  color: #7c3aed;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
}
.dark-mode .interest-badge { background: rgba(168, 85, 247, 0.2); color: #a855f7; }
.view-details-btn {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  border: none;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}
.view-details-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4); }

/* Lead Detail Dialog Styles */
.lead-detail-card .section-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}
.lead-detail-card.theme--dark .section-title,
.theme--dark .lead-detail-card .section-title { color: #ffffff; }
.lead-detail-card .info-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.lead-detail-card.theme--dark .info-label,
.theme--dark .lead-detail-card .info-label { color: rgba(255,255,255,0.7); }
.lead-detail-card .info-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}
.lead-detail-card.theme--dark .info-value,
.theme--dark .lead-detail-card .info-value { color: #ffffff; }
.lead-detail-card .info-value a {
  color: #6366f1;
  text-decoration: none;
}
.lead-detail-card.theme--dark .info-value a,
.theme--dark .lead-detail-card .info-value a { color: #818cf8; }
.lead-detail-card .info-value a:hover { text-decoration: underline; }
.lead-detail-card .vehicle-name-large {
  font-size: 16px;
  font-weight: 600;
}
.lead-detail-card .vehicle-preview-img {
  border-radius: 8px;
  background: #f1f5f9;
}
.lead-detail-card.theme--dark .vehicle-preview-img { background: rgba(255,255,255,0.1); }

/* Inventory Card */
.inventory-tabs { display: flex; gap: 8px; }
.inventory-tabs button {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}
.inventory-tabs button.active {
  background: #6366f1;
  border-color: #6366f1;
  color: #fff;
}
.dark-mode .inventory-tabs button { background: transparent; border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.7); }
.dark-mode .inventory-tabs button.active {
  background: rgba(168, 85, 247, 0.2);
  border-color: #a855f7;
  color: #fff;
}
.inventory-filters { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.inventory-filters select {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 11px;
  color: #475569;
}
.dark-mode .inventory-filters select { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: #fff; }
.filter-label { padding: 6px 12px; border-radius: 6px; font-size: 11px; color: #64748b; }
.filter-label.active { background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff; }
.inventory-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; }
.inventory-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
.dark-mode .inventory-item { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); }
.vehicle-image img { width: 100%; height: 100px; object-fit: cover; background: #e2e8f0; }
.vehicle-name { padding: 8px 8px 0; font-size: 12px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dark-mode .vehicle-name { color: #fff; }
.vehicle-badge { padding: 4px 8px 8px; font-size: 10px; text-align: left; }
.vehicle-badge.network { color: #3b82f6; }
.vehicle-badge.autosync { color: #7c3aed; }
.dark-mode .vehicle-badge.network { color: #60a5fa; }
.dark-mode .vehicle-badge.autosync { color: #a855f7; }
.vehicle-actions { display: flex; gap: 8px; padding: 8px; }
.action-btn {
  flex: 1;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 6px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  color: #475569;
  transition: all 0.2s;
}
.dark-mode .action-btn { background: rgba(255,255,255,0.1); border-color: transparent; color: #fff; }
.action-btn.primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff; border: none; }

/* Quick Stats Card */
.stats-card { padding: 16px; }
.stat-section { margin-bottom: 20px; }
.stat-label { font-size: 12px; color: #64748b; margin-bottom: 8px; display: block; }
.dark-mode .stat-label { color: rgba(255,255,255,0.6); }
.mini-chart-container { position: relative; }
.line-chart { width: 100%; height: 60px; }
.line-chart polyline { stroke: #6366f1; }
.dark-mode .line-chart polyline { stroke: #a855f7; }
.chart-labels { display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; margin-top: 4px; }
.dark-mode .chart-labels { color: rgba(255,255,255,0.4); }
.stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.stat-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.dark-mode .stat-box { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); }
.stat-box .stat-icon { color: #22c55e; }
.stat-box .stat-content { display: flex; flex-direction: column; }
.stat-box .stat-label { font-size: 10px; margin-bottom: 2px; color: #64748b; }
.dark-mode .stat-box .stat-label { color: rgba(255,255,255,0.6); }
.stat-box .stat-value { font-size: 16px; font-weight: 700; color: #1e293b; }
.dark-mode .stat-box .stat-value { color: #fff; }
.circular-stat { text-align: center; }
.circular-progress { position: relative; width: 100px; height: 100px; margin: 0 auto; }
.circular-progress svg { transform: rotate(-90deg); }
.circular-progress svg circle:first-child { stroke: #e2e8f0; }
.dark-mode .circular-progress svg circle:first-child { stroke: #3b3b5c; }
.circular-value { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; }
.circular-value .label { font-size: 10px; color: #64748b; display: block; }
.dark-mode .circular-value .label { color: rgba(255,255,255,0.5); }
.circular-value .value { font-size: 24px; font-weight: 700; color: #22c55e; }
.bar-stat .bar-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.sale-badge { background: #22c55e; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 10px; }
.bar-chart { display: flex; align-items: flex-end; gap: 8px; height: 60px; margin-bottom: 8px; }
.bar { flex: 1; background: rgba(99, 102, 241, 0.2); border-radius: 4px 4px 0 0; }
.dark-mode .bar { background: rgba(168, 85, 247, 0.3); }
.bar.active { background: #6366f1; }
.dark-mode .bar.active { background: #a855f7; }
.bar-value { font-size: 32px; font-weight: 700; text-align: center; color: #1e293b; }
.dark-mode .bar-value { color: #fff; }

/* Empty State */
.empty-state {
  text-align: center;
  padding: 24px;
  color: #64748b;
}
.dark-mode .empty-state { color: rgba(255,255,255,0.5); }

/* Responsive */
@media (max-width: 1400px) {
  .dashboard-wrapper { margin-left: 250px; }
  .dashboard-main { grid-template-columns: 1fr 1fr; }
  .right-column { grid-column: span 2; }
}
@media (max-width: 1200px) {
  .dashboard-main { grid-template-columns: 1fr; }
  .left-column, .right-column { display: flex; flex-wrap: wrap; gap: 16px; }
  .left-column .glass-card, .right-column .glass-card { flex: 1; min-width: 280px; }
  .right-column { grid-column: span 1; }
}
@media (max-width: 960px) {
  .dashboard-wrapper { margin-left: 0; padding-top: 60px; }
  .middle-column { order: -1; }
}
@media (max-width: 768px) {
  .dashboard-subheader { flex-wrap: wrap; gap: 16px; padding: 16px; }
  .dashboard-main { padding: 0 12px 16px; gap: 16px; }
  .glass-card { padding: 16px; }
  .inventory-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  /* Lead item mobile layout */
  .lead-item {
    grid-template-columns: 60px 1fr;
    gap: 10px;
    padding: 12px 0;
  }
  .lead-image img { width: 60px; height: 45px; }
  .lead-info { grid-column: span 1; }
  .lead-details {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }
  .lead-actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
  }
  .view-details-btn { width: auto; flex: 1; max-width: 120px; }
  /* Card header mobile */
  .leads-card .card-header, .inventory-card .card-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  .inventory-tabs { order: 3; width: 100%; }
  .inventory-tabs button { flex: 1; }
  /* Overview card mobile */
  .overview-value .value { font-size: 20px; }
  .overview-label { font-size: 12px; }
}
@media (max-width: 480px) {
  .dashboard-main { padding: 0 8px 16px; }
  .glass-card { padding: 12px; border-radius: 12px; }
  .card-title { font-size: 14px; margin-bottom: 12px; }
  .overview-value .value { font-size: 18px; }
  .overview-item { padding: 10px 0; }
  /* Lead item very small screens */
  .lead-item {
    grid-template-columns: 1fr;
    text-align: left;
  }
  .lead-image { display: none; }
  .lead-name { font-size: 13px; }
  .lead-vehicle { font-size: 12px; }
  .lead-meta { font-size: 10px; }
  .view-details-btn {
    padding: 8px 12px;
    font-size: 11px;
    max-width: none;
  }
  .interest-badge { font-size: 9px; padding: 3px 8px; }
  /* Inventory mobile */
  .inventory-item {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .inventory-item-img {
    width: 100%;
    height: 120px;
    margin-bottom: 8px;
  }
  /* Lead dialog mobile */
  .lead-detail-card .v-card__text { padding: 12px !important; }
  .lead-detail-card .info-label { font-size: 10px; }
  .lead-detail-card .info-value { font-size: 13px; }
  .lead-detail-card .vehicle-name-large { font-size: 14px; }
}

/* Delete Image Button */
.delete-img-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}
.v-card.pa-1 {
  position: relative;
}

/* Referring Agent Card Styles */
.agent-referral-card {
  margin-top: 16px;
}
.agent-info-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}
.agent-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.agent-avatar .avatar-placeholder {
  font-size: 32px;
  color: white;
}
.agent-details {
  flex: 1;
}
.agent-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}
.agent-contact {
  margin-top: 4px;
}
.agent-contact .contact-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.2s;
}
.agent-contact .contact-link:hover {
  color: #4ade80;
}
.agent-contact .contact-link .material-icons-round {
  font-size: 14px;
}
.commission-info-box {
  background: linear-gradient(135deg, rgba(74, 222, 128, 0.1) 0%, rgba(34, 197, 94, 0.1) 100%);
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}
.commission-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  margin-bottom: 4px;
}
.commission-amount {
  font-size: 28px;
  font-weight: 700;
  color: #4ade80;
}
.commission-note {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 8px;
  line-height: 1.4;
}
</style>
